import jsPDF from 'jspdf';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { projectService } from '../services/projectService';

export const exportPropertyReportToPDF = async (
  propertyInfo,
  mapImageData,
  projectName = 'Property',
  projectId = null
) => {
  try {
    // Create PDF in portrait mode
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });

    // Get page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    // Add header
    pdf.setFontSize(20);
    pdf.setFont(undefined, 'bold');
    pdf.text('Property Report', margin, 20);

    // Add project name
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'normal');
    pdf.text(projectName, margin, 30);

    // Add timestamp
    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 37);
    pdf.setTextColor(0);

    // Add horizontal line
    pdf.setLineWidth(0.5);
    pdf.line(margin, 40, pageWidth - margin, 40);

    let yPosition = 50;

    // Property Information Section
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Property Information', margin, yPosition);
    yPosition += 10;

    pdf.setFontSize(11);
    pdf.setFont(undefined, 'normal');

    // Property details
    const details = [
      { label: 'Address:', value: propertyInfo.address || 'N/A' },
      { label: 'APN:', value: propertyInfo.apn || 'N/A' },
      { label: 'Use Type:', value: propertyInfo.useType || 'N/A' },
      { label: 'Year Built:', value: propertyInfo.yearBuilt || 'N/A' },
      {
        label: 'Square Feet:',
        value: propertyInfo.squareFeet
          ? propertyInfo.squareFeet.toLocaleString()
          : 'N/A',
      },
      { label: 'Units:', value: propertyInfo.units || 'N/A' },
      {
        label: 'Land Value:',
        value: propertyInfo.landValue
          ? `$${propertyInfo.landValue.toLocaleString()}`
          : 'N/A',
      },
      {
        label: 'Improvement Value:',
        value: propertyInfo.improvementValue
          ? `$${propertyInfo.improvementValue.toLocaleString()}`
          : 'N/A',
      },
      {
        label: 'Total Value:',
        value: propertyInfo.totalValue
          ? `$${propertyInfo.totalValue.toLocaleString()}`
          : 'N/A',
      },
    ];

    details.forEach((detail) => {
      pdf.setFont(undefined, 'bold');
      pdf.text(detail.label, margin, yPosition);
      pdf.setFont(undefined, 'normal');
      pdf.text(detail.value, margin + 40, yPosition);
      yPosition += 7;
    });

    // Add map section
    yPosition += 10;

    // Check if we need a new page
    if (yPosition > pageHeight - 150) {
      pdf.addPage();
      yPosition = 20;
    }

    // Add map title
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Parcel Map', margin, yPosition);
    yPosition += 10;

    if (mapImageData) {
      try {
        // Calculate map dimensions
        const maxMapWidth = pageWidth - 2 * margin;
        const maxMapHeight = 120; // Fixed height for map

        console.log(
          'Adding map image to PDF:',
          typeof mapImageData === 'string' ? 'URL' : 'base64'
        );

        // Add map image (works with both URLs and base64 data)
        pdf.addImage(
          mapImageData,
          'PNG',
          margin,
          yPosition,
          maxMapWidth,
          maxMapHeight,
          undefined,
          'FAST'
        );

        yPosition += maxMapHeight + 10;
      } catch (error) {
        console.error('Failed to add map image to PDF:', error);
        // If image fails, add a note
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'italic');
        pdf.setTextColor(100);
        pdf.text(
          'Map could not be loaded. View the interactive map online.',
          margin,
          yPosition
        );
        pdf.setTextColor(0);
        yPosition += 20;
      }
    } else {
      // Add note about map
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100);
      pdf.text(
        'Map preview not available in PDF. View the interactive map online.',
        margin,
        yPosition
      );
      pdf.setTextColor(0);
      yPosition += 20;
    }

    // Add footer
    pdf.setFontSize(8);
    pdf.setTextColor(100);
    const footerText = 'Data source: Los Angeles County GIS Services';
    const textWidth = pdf.getTextWidth(footerText);
    pdf.text(footerText, (pageWidth - textWidth) / 2, pageHeight - 10);

    // Add disclaimer
    pdf.setFontSize(7);
    const disclaimer =
      'This report is for informational purposes only. For official records, contact the LA County Assessor.';
    const disclaimerWidth = pdf.getTextWidth(disclaimer);
    pdf.text(disclaimer, (pageWidth - disclaimerWidth) / 2, pageHeight - 5);

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `property_report_${propertyInfo.apn || 'unknown'}_${timestamp}.pdf`;

    // Get PDF as blob
    const pdfBlob = pdf.output('blob');

    // Save to Firebase Storage if projectId is provided
    let downloadUrl = null;
    if (projectId) {
      try {
        // Upload to Firebase Storage
        const storagePath = `projects/${projectId}/reports/${filename}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, pdfBlob);
        downloadUrl = await getDownloadURL(storageRef);

        // Update project document with PDF URL
        await projectService.updateProject(projectId, {
          lastPropertyReportUrl: downloadUrl,
          lastPropertyReportDate: new Date(),
          lastPropertyReportFilename: filename,
        });
      } catch (storageError) {
        console.error('Error uploading PDF to Firebase:', storageError);
        // Continue with local download even if Firebase upload fails
      }
    }

    // Download the PDF locally
    pdf.save(filename);

    return { success: true, filename, downloadUrl };
  } catch (error) {
    console.error('Error generating property report PDF:', error);
    return { success: false, error: error.message };
  }
};

export default exportPropertyReportToPDF;
